// ----------------------
// FULL RESPONSIVE VERSION
// ----------------------

import React, { useState, useEffect } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Divider,
  Avatar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  IconButton,
  InputAdornment,
  Snackbar,
  Alert,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import PeopleIcon from "@mui/icons-material/People";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import { sessionsService } from "..//../Services/SessionsService";
import { KycUserService } from "..//../Services/KycUserService";
import { useDispatch, useSelector } from "react-redux";

const Dashboard = () => {
  const [stats, setStats] = useState([
    { title: "Total KYCs Today", value: 0, icon: <PeopleIcon />, color: "#2196f3" },
    { title: "Pending KYCs", value: 0, icon: <AccessTimeIcon />, color: "#ff9800" },
    { title: "Approved KYCs", value: 0, icon: <CheckCircleIcon />, color: "#4caf50" },
    { title: "Declined KYCs", value: 0, icon: <CancelIcon />, color: "#f44336" },
  ]);

  const [recentActivity, setRecentActivity] = useState([]);
  const [filteredActivity, setFilteredActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openAddCustomer, setOpenAddCustomer] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [customerData, setCustomerData] = useState({ name: "", email: "", mobile: "" });
  const { user } = useSelector((state) => state.auth);
  const role = user?.role?.name || user?.role;

  const customerSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    email: Yup.string().email("Enter a valid email").required("Email is required"),
    mobile: Yup.string()
      .required("Mobile number is required")
      .matches(/^\d{10}$/, "Enter a valid 10-digit mobile number"),
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleAddCustomerOpen = () => setOpenAddCustomer(true);
  const handleAddCustomerClose = () => setOpenAddCustomer(false);

  const handleClose = () => {
    setSnackbar({ open: false, message: "", severity: "success" });
  };

  // 🔍 Filter search
  useEffect(() => {
    const q = searchQuery.toLowerCase();
    setFilteredActivity(
      recentActivity.filter(
        (a) => a.name.toLowerCase().includes(q) || a.kycId.toLowerCase().includes(q)
      )
    );
  }, [searchQuery, recentActivity]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        const response = await sessionsService.getSessions(user, 100, 0, true);
        const sessions = response.data || response.items || [];
        const today = new Date().toLocaleDateString("en-IN");

        const todaySessions = sessions.filter((s) => {
          const sessionDate = new Date(
            s.createdAt || s.updatedAt
          ).toLocaleDateString("en-IN");
          return sessionDate === today;
        });

        const pending = sessions.filter(
          (s) =>
            s.applicant_id?.auditor_status !== "approved" &&
            s.applicant_id?.auditor_status !== "rejected"
        ).length;

        const approved = sessions.filter(
          (s) => s.applicant_id?.auditor_status === "approved"
        ).length;

        const declined = sessions.filter(
          (s) => s.applicant_id?.auditor_status === "rejected"
        ).length;

        setStats([
          { title: "Total KYCs Today", value: todaySessions.length, icon: <PeopleIcon />, color: "#2196f3" },
          { title: "Pending KYCs", value: pending, icon: <AccessTimeIcon />, color: "#ff9800" },
          { title: "Approved KYCs", value: approved, icon: <CheckCircleIcon />, color: "#4caf50" },
          { title: "Declined KYCs", value: declined, icon: <CancelIcon />, color: "#f44336" },
        ]);

        const recentSessions = await Promise.all(
          sessions.slice(0, 5).map(async (s) => {
            let decryptedName = s.applicant_id?.name_enc || "Unknown User";

            if (s.applicant_id?.name_enc && s.applicant_id?.applicant_id) {
              try {
                const decryptResult = await KycUserService.decryptApplicantData(
                  {
                    applicant_id: s.applicant_id.applicant_id,
                    encrypted_name: s.applicant_id.name_enc,
                  },
                  user
                );
                decryptedName = decryptResult.data?.decrypted_name || decryptedName;
              } catch (_) {}
            }

            return {
              name: decryptedName,
              kycId: s.applicant_id?.applicant_id || "N/A",
              date: new Date(
                s.createdAt || s.timestamps?.updated_at
              ).toLocaleString("en-IN", {
                timeZone: "Asia/Kolkata",
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
              }),
              status: s.applicant_id?.auditor_status,
            };
          })
        );

        setRecentActivity(recentSessions);
        setFilteredActivity(recentSessions);
      } catch (error) {
        console.error("Dashboard error", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <Box p={3} display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const autofillFix = {
    "& input:-webkit-autofill": {
      WebkitBoxShadow: "0 0 0 1000px #fff inset",
      WebkitTextFillColor: "#000",
    },
  };

  return (
    <Box sx={{ p: 2, pt: 1, mt: -2 }}>

      {/* HEADER */}
      <Typography variant="h5" fontWeight="bold">
        Dashboard
      </Typography>

      <Typography variant="body2" color="text.secondary" mb={2}>
        KYC verification overview and statistics
      </Typography>

      {/* ---------------------- */}
      {/* RESPONSIVE STATS */}
      {/* ---------------------- */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" },
          gap: 2,
          mb: 3,
        }}
      >
        {stats.map((item, index) => (
          <Card
            key={index}
            sx={{
              borderRadius: "12px",
              boxShadow: "0px 1px 4px rgba(0,0,0,0.08)",
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="body2" fontWeight={600} color="text.secondary">
                  {item.title}
                </Typography>

                <Box
                  sx={{
                    backgroundColor: `${item.color}20`,
                    color: item.color,
                    p: 1,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {item.icon}
                </Box>
              </Box>

              <Typography variant="h4" fontWeight="bold" color={item.color}>
                {item.value}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* ---------------------- */}
      {/* SEARCH & ADD CUSTOMER */}
      {/* ---------------------- */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 3,
          alignItems: "center", 
        }}
      >
        <TextField
          placeholder="Search by email, name, or ID..."
          variant="outlined"
          size="small"
          fullWidth
          sx={{
            flex: 1, 
            backgroundColor: "#fff",
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9ca3af", fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddCustomerOpen}
          disabled={role !== "Admin"}
          sx={{
            height: "42px",
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 600,
            width: { xs: "100%", sm: "auto" },
          }}
        >
          Add Customer
        </Button>
      </Box>

      {/* SNACKBAR */}
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleClose}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>

      {/* Add Customer Dialog */}
      <Dialog
        open={openAddCustomer}
        onClose={handleAddCustomerClose}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: "500px",
            borderRadius: "12px",
            boxShadow: "0 8px 40px rgba(0, 0, 0, 0.25)",
            backgroundColor: "#fff",
          },
        }}
        BackdropProps={{
          sx: { backgroundColor: "rgba(0,0,0,0.35)" },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: "1.25rem",
            color: "#111827",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3.5,
            pt: 3,
            pb: 0.5,
          }}
        >
          Customer Video KYC Initiation
          <IconButton
            onClick={handleAddCustomerClose}
            size="small"
            sx={{
              color: "#6b7280",
              "&:hover": { background: "#f5f5f5" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Typography
          sx={{
            color: "#6b7280",
            fontSize: "0.92rem",
            px: 3.5,
            pb: 1.5,
            pt: 0.5,
          }}
        >
          Please fill in the required information to initiate the KYC process.
        </Typography>

        <Box sx={{ borderTop: "1px solid #f0f0f0" }} />

        <DialogContent sx={{ px: 3.5, pt: 2.5, pb: 3 }}>
          <Formik
            initialValues={customerData}
            validationSchema={customerSchema}
            validateOnChange={true} 
            validateOnBlur={true}   
            onSubmit={async (values, { resetForm }) => {
              try {
                await KycUserService.createUser(values, user);
                setSnackbar({
                  open: true,
                  message: "Customer created successfully. Verification link has been sent via email for Video KYC.",
                  severity: "success",
                });
                resetForm();
                setOpenAddCustomer(false);
              } catch (error) {
                setSnackbar({
                  open: true,
                  message: error?.response?.data?.detail || "Something went wrong",
                  severity: "error",
                });
              }
            }}
          >
            {({ values, errors, touched, handleChange, handleSubmit, handleBlur }) => (
              <form onSubmit={handleSubmit}>
                {/* Name */}
                <Box mb={2.5}>
                  <Typography sx={{ mb: 0.7, fontWeight: 500, color: "#111827" }}>
                    Name
                  </Typography>
                  <TextField
                    name="name"
                    placeholder="John Doe"
                    fullWidth
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}  
                    error={Boolean(errors.name)}
                    helperText={errors.name}
                    sx={{
                      ...autofillFix,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        height: "44px",
                        fontSize: "0.95rem",
                      },
                    }}
                  />
                </Box>

                {/* Email */}
                <Box mb={2.5}>
                  <Typography sx={{ mb: 0.7, fontWeight: 500, color: "#111827" }}>
                    Email
                  </Typography>
                  <TextField
                    name="email"
                    placeholder="example@gmail.com"
                    fullWidth
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}  
                    error={Boolean(errors.email)}
                    helperText={errors.email}
                    sx={{
                      ...autofillFix,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        height: "44px",
                        fontSize: "0.95rem",
                      },
                    }}
                  />
                </Box>

                {/* Mobile */}
                <Box>
                  <Typography sx={{ mb: 0.7, fontWeight: 500, color: "#111827" }}>
                    Mobile Number
                  </Typography>
                  <TextField
                    name="mobile"
                    placeholder="1234567890"
                    fullWidth
                    value={values.mobile}
                    onChange={(e) => {
                      // Allow only digits and limit to 10 characters
                      const onlyNums = e.target.value.replace(/\D/g, "");
                      if (onlyNums.length <= 10) {
                        handleChange({
                          target: {
                            name: "mobile",
                            value: onlyNums,
                          },
                        });
                      }
                    }}
                    onBlur={handleBlur}  
                    error={Boolean(errors.mobile)}
                    helperText={errors.mobile}
                    inputProps={{ maxLength: 10, inputMode: "numeric", pattern: "[0-9]*" }}
                    sx={{
                      ...autofillFix,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        height: "44px",
                        fontSize: "0.95rem",
                      },
                    }}
                  />
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  sx={{
                    mt: 3,
                    py: 1.4,
                    fontWeight: 600,
                    fontSize: "1rem",
                    borderRadius: "8px",
                    textTransform: "none",
                    boxShadow: "none",
                  }}
                >
                  Submit
                </Button>
              </form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>

      {/* Recent Activity */}
      <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" mb={2}>
            Recent Activity
          </Typography>

          {filteredActivity.length === 0 ? (
            <Typography variant="body2" color="text.secondary" align="center">
              No matching records found
            </Typography>
          ) : (
            filteredActivity.map((activity, index) => (
              <Box key={index}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", sm: "center" },
                    gap: { xs: 1.5, sm: 0 },
                    py: 1.2,
                  }}
                >
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ bgcolor: "#e3f2fd", color: "#2196f3", mr: 2 }}>
                      <PeopleIcon />
                    </Avatar>
                    <Box>
                      <Typography fontWeight="bold">{activity.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        KYC ID: {activity.kycId}
                      </Typography>
                    </Box>
                  </Box>

                  <Box textAlign={{ xs: "left", sm: "right" }}>
                    <Typography variant="body2" color="text.secondary">
                      {activity.date}
                    </Typography>
                    <Chip
                      label={activity.status.charAt(0).toUpperCase() + activity.status.slice(1)}
                      color={
                        activity.status === "approved"
                          ? "success"
                          : activity.status === "rejected"
                          ? "error"
                          : "warning"
                      }
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                </Box>

                {index < filteredActivity.length - 1 && <Divider />}
              </Box>
            ))
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Dashboard;
